import isEmpty from "lodash/isEmpty";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  useDeleteUploadIngredientImage,
  useGetIngredientPackaging,
  usePutIngredientDataToQueue,
  useUploadIngredientImage
} from "../../../../api/ingredients";
import { useGetCountries, useGetProduceTypes, useGetSpecifyIngredient } from "../../../../api/static-data";
import { IngredientDataType } from "../../../../config/enums/IngredientType.enum";
import { MappingSourceType } from "../../../../config/enums/MappingSourceType.enum";
import { errorView } from "../../../../helpers/ErrorHandler";
import { useSupplierDataType } from "../../../ingredients/ingredientRegister/hooks/useSupplierDataType";
import { useSupplierFieldState } from "../../../ingredients/ingredientRegister/hooks/useSupplierFieldState";
import { useSupplierRegisterForm } from "../../../ingredients/ingredientRegister/hooks/useSupplierRegisterForm";
import { useSupplierSalesUnitSync } from "../../../ingredients/ingredientRegister/hooks/useSupplierSalesUnitSync";
import { useSupplierSave } from "../../../ingredients/ingredientRegister/hooks/useSupplierSave";
import { useSupplierSelection } from "../../../ingredients/ingredientRegister/hooks/useSupplierSelection";
import { SupplierIngredientLcaDataViewRef } from "../../../ingredients/shared/components/SupplierIngredientLcaDataView";
import { SupplierDataShape } from "../../../ingredients/ingredientRegister/supplierRegister.types";
import { derivePackageFlags, getIngredientId, getSupplierId } from "../../../ingredients/ingredientRegister/supplierRegister.utils";
import { IngredientRegisterButtonState } from "../../ingredientRegister/services/IngredientRegisterButtonState";
import { IngredientRegisterOptions } from "../../ingredientRegister/services/IngredientRegisterOptions";
import { IngredientRegisterViewModel } from "../../ingredientRegister/types/ingredientRegisterView.types";
import { IngredientRowViewModel } from "../../ingredientList/types/ingredientListView.types";

interface UseIngredientEditViewModelParams {
  ingredientIdToUpdate: IngredientRowViewModel | undefined;
  visibleModal: boolean;
  setVisibleModal: (value: boolean) => void;
  refetchIngredients: () => void;
  isEnableEnvironmentData?: boolean;
}

/**
 * Edit-flow counterpart to `useIngredientRegisterViewModel` — same 6
 * protected legacy hooks (`useSupplierFieldState`, `useSupplierRegisterForm`,
 * `useSupplierSalesUnitSync`, `useSupplierDataType`, `useSupplierSelection`,
 * `useSupplierSave`), called directly rather than through `SupplierRegister.tsx`,
 * for the same reason: so the existing-supplier branch can render new
 * Tailwind markup. Returns the exact `IngredientRegisterViewModel` shape so
 * `UnitDetailsCard`/`EnvironmentalDataCard`/`RegisterFormProvider`/
 * `IngredientRegisterFooter`/`CalculateConfirmDialog`/`PackagingSection`/
 * `TransportationSection`/`LcaDataSection` — all built for the register flow
 * — can be reused here unchanged; the register-only fields (ingredient/
 * supplier search, "+ New" flows) are filled with inert placeholders since
 * nothing in the edit UI renders them (editing an ingredient never searches
 * for one — see `IngredientEditInfoCard.tsx`).
 *
 * Hydration is simpler here than in the register flow: the legacy
 * `SupplierRegister.tsx`'s `loadSupplierEditData()` (only reachable when
 * `disableEdit` is true) hydrates directly and synchronously from the row
 * already passed in (`ingredientIdToUpdate`) via `saveValues`, with no
 * search-result round trip — and `useSupplierSalesUnitSync`'s own second,
 * async hydration wave never fires here (it's gated on `isSupplierExist`,
 * which depends on an `ingredient_data.suppliers` list this row shape
 * doesn't have), so the "keep the first value" guard below never actually
 * needs to reject anything in practice. It's kept anyway for parity and
 * because it's a no-op unless that race exists.
 */
export const useIngredientEditViewModel = ({
  ingredientIdToUpdate,
  visibleModal,
  setVisibleModal,
  refetchIngredients,
  isEnableEnvironmentData = false
}: UseIngredientEditViewModelParams): IngredientRegisterViewModel => {
  const navigation = useNavigate();
  const lcaRef = useRef<SupplierIngredientLcaDataViewRef>(null);

  const [ingredientData, setIngredientDataState] = useState<Record<string, unknown> | undefined>(
    undefined
  );
  const [imagePath, setImagePath] = useState("");
  const [hideIngredientView, setHideIngredientView] = useState(false);
  const [, setCurrentDataType] = useState<string>(IngredientDataType.SUPPLIER);
  const [viewEnvironmentalData, setViewEnvironmentalData] = useState(false);
  const [currentSupplierId, setCurrentSupplierId] = useState<unknown>(null);
  const [validateData, setValidateData] = useState({
    country: 1,
    specifyIngredientValue: "",
    produceType: "",
    ingredientType: ""
  });
  const [calculateModalOpen, setCalculateModalOpen] = useState(false);
  const [supplierValidation, setSupplierValidation] = useState(false);
  const [isSupplierFormOpen, setIsSupplierFormOpen] = useState(false);
  const [supplierData, setSupplierData] = useState<SupplierDataShape>({});

  // Editing an ingredient never searches for one — this row is the whole
  // selection, set once and re-synced if the caller passes a different row.
  useEffect(() => {
    setIngredientDataState(ingredientIdToUpdate as unknown as Record<string, unknown>);
    if (ingredientIdToUpdate?.supplier_id) setCurrentSupplierId(ingredientIdToUpdate.supplier_id);
  }, [ingredientIdToUpdate]);

  useEffect(() => {
    setImagePath((ingredientData?.ingredient_image_path as string) || "");
  }, [ingredientData]);

  // Register's two search fields don't exist in the edit UI (Ingredient/
  // Supplier Name are shown read-only) — this form only exists so the
  // returned view-model satisfies `IngredientRegisterViewModel`'s shape.
  const { control: searchControl, formState: { errors: searchErrors } } = useForm<
    Record<string, unknown>
  >();

  const ingredientId = getIngredientId(ingredientData);
  const supplierId = getSupplierId(supplierData);
  const registerPairKey = `${ingredientId || ""}:${supplierId || ""}`;

  const supplierAsUser = Boolean(
    ingredientData?.supplierAsUser || supplierData?.supplier_data?.is_supplier_as_user
  );

  const { data: countriesData, isSuccess: isCountriesDataSuccess, isLoading: isCountriesDataLoading } =
    useGetCountries();
  const { data: produceTypesData, isSuccess: isProduceTypesSuccess, isLoading: isProduceTypesLoading } =
    useGetProduceTypes();
  const { data: specifyIngredientData } = useGetSpecifyIngredient();

  const { data: ingredientPackageData, refetch: refetchPackagingData } = useGetIngredientPackaging({
    ingredientId,
    supplierId,
    // `ingredientId`/`supplierId` hydrate one render apart (see the two
    // useEffects above), so gating on `visibleModal` alone fires this
    // request with one or both ids still undefined
    // (/ingredient/packaging/undefined/undefined). Wait for both to be
    // present before fetching, matching the register flow's
    // `loadPackageDataFlag` gate.
    loadPackageData: visibleModal && Boolean(ingredientId) && Boolean(supplierId)
  });

  const { mutate: uploadIngredientImage } = useUploadIngredientImage();
  const { mutate: deleteIngredientImage } = useDeleteUploadIngredientImage();

  const onImageUpload = useCallback(
    (file: File | null) => {
      if (!file) return;
      setImagePath(URL.createObjectURL(file));
      uploadIngredientImage({ ingredient_id: ingredientId, file });
    },
    [ingredientId, uploadIngredientImage]
  );

  const onImageDelete = useCallback(() => {
    setImagePath("");
    deleteIngredientImage({ ingredient_id: ingredientId });
  }, [ingredientId, deleteIngredientImage]);

  // Lets the user rename the ingredient. `updateIngredientData` (inside
  // useSupplierSave) reads `ingredientData.ingredient_name` on every save, so
  // updating this state here is enough for the edited name to reach the
  // PUT /ingredient payload — no other wiring needed.
  const onIngredientNameChange = useCallback((value: string) => {
    setIngredientDataState(prev => ({ ...(prev || {}), ingredient_name: value }));
  }, []);

  const saveIngredientImage = useCallback(() => {
    // No-op here: unlike the register flow (which stages a file until the
    // ingredient exists), edit mode uploads immediately via onImageUpload
    // above — this is only present to satisfy useSupplierSave's contract.
  }, []);

  const {
    newSupplier,
    setNewSupplier,
    ingredientType,
    setIngredientType,
    defaultType,
    setDefaultType,
    country,
    setCountry,
    supplierCountry,
    setSupplierCountry,
    isSupplierAsUser,
    setIsSupplierAsUser,
    produceType,
    setProduceType,
    farmingType,
    setFarmingType,
    fieldType,
    setFieldType,
    specifyIngredientValue,
    setSpecifyIngredientValue,
    isSupplierExist,
    setIsSupplierExist,
    resetFields,
    applyIngredientDetails
  } = useSupplierFieldState({
    currentDataType: setCurrentDataType,
    loadPackageData: () => {},
    getDefaultCountry: () => (countriesData as unknown as Array<{ id: number }> | undefined)?.[0]?.id
  });

  // Same "keep the first hydrated value for this pairing" guard as the
  // register flow's view-model — see its file for the full rationale.
  const ingredientTypePairRef = useRef<string | null>(null);
  const isIngredientTypeUserEditRef = useRef(false);
  const lockedIngredientTypeRef = useRef(ingredientType);
  if (ingredientTypePairRef.current !== registerPairKey) {
    ingredientTypePairRef.current = registerPairKey;
    lockedIngredientTypeRef.current = ingredientType;
    isIngredientTypeUserEditRef.current = false;
  } else if (isIngredientTypeUserEditRef.current) {
    lockedIngredientTypeRef.current = ingredientType;
    isIngredientTypeUserEditRef.current = false;
  }
  const lockedIngredientType = lockedIngredientTypeRef.current;

  const {
    register,
    handleSubmit,
    setValue,
    control,
    errors,
    registerNewSupplier,
    resetNewSupplier,
    handleSubmitNewSupplier,
    controlNewSupplier,
    errorsNewSupplier
  } = useSupplierRegisterForm({
    newSupplier,
    country,
    specifyIngredientValue,
    produceType,
    ingredientType: lockedIngredientType,
    setSupplierValidation,
    setIsSupplierFormOpen,
    onChangeValidateData: setValidateData
  });

  // `useSupplierSalesUnitSync`'s own live-fetch hydration only fires when
  // `isSupplierExist` is true, which needs an `ingredient_data.suppliers`
  // list this edit row doesn't carry — so `ingredientSuppliers` is
  // deliberately left undefined, matching the legacy edit flow exactly
  // (it never populates this either).
  const { salesUnitData, isSalesUnitDataSuccess } = useSupplierSalesUnitSync({
    ingredientId,
    supplierId,
    isSupplierExist,
    setIsSupplierExist,
    ingredientSuppliers: undefined,
    selectedSupplierId: supplierData?.supplier_data?._id,
    applyIngredientDetails,
    setViewEnvironmentalData,
    setValue
  });

  const { effectiveDataType, setDataType } = useSupplierDataType({
    supplierData,
    setSupplierData,
    salesUnitData,
    supplierAsUser,
    currentDataType: setCurrentDataType
  });

  const dataTypePairRef = useRef<string | null>(null);
  const isDataTypeUserEditRef = useRef(false);
  const lockedDataTypeRef = useRef(effectiveDataType);
  if (dataTypePairRef.current !== registerPairKey) {
    dataTypePairRef.current = registerPairKey;
    lockedDataTypeRef.current = effectiveDataType;
    isDataTypeUserEditRef.current = false;
  } else if (isDataTypeUserEditRef.current) {
    lockedDataTypeRef.current = effectiveDataType;
    isDataTypeUserEditRef.current = false;
  }
  const lockedDataType = lockedDataTypeRef.current;

  const { closeSupplierData, saveValues } = useSupplierSelection({
    ingredientId,
    resetFields,
    applyIngredientDetails,
    setValue,
    setViewEnvironmentalData,
    setNewSupplier,
    setIsSupplierFormOpen,
    setIsSupplierAsUser,
    hideIngredientView: setHideIngredientView,
    resetNewSupplier,
    supplierCurrentData: data => setSupplierData(data || {}),
    setSupplierData,
    loadPackageData: () => {},
    checkCurrentSupplier: setCurrentSupplierId
  });

  const { saveData, handleSupplierSave } = useSupplierSave({
    supplierData,
    ingredientData,
    effectiveDataType: lockedDataType,
    viewEnvironmentalData,
    lcaRef,
    ingredientType: lockedIngredientType,
    defaultType,
    country,
    specifyIngredientValue,
    produceType,
    farmingType,
    fieldType,
    supplierCountry,
    isSupplierAsUser,
    saveValues,
    refetchSupplier: () => {},
    setIngredientData: data => setIngredientDataState(data as Record<string, unknown> | undefined),
    saveIngredientImage,
    setSupplierData,
    supplierCurrentData: data => setSupplierData(data || {}),
    checkCurrentSupplier: setCurrentSupplierId,
    resetNewSupplier,
    setNewSupplier,
    setIsSupplierFormOpen,
    hideIngredientView: setHideIngredientView,
    setIsSupplierAsUser
  });

  const packageFlags = useMemo(
    () => derivePackageFlags(lockedDataType, lockedIngredientType),
    [lockedDataType, lockedIngredientType]
  );

  // Mirrors `SupplierRegister.tsx`'s `loadSupplierEditData()` — only
  // reachable there when `disableEdit` is true, which is always the case in
  // this edit flow. Hydrates directly from the row already in hand, no
  // search-result lookup needed.
  //
  // Guarded to run once per `ingredientIdToUpdate` (the prop, not the
  // `ingredientData` state) rather than on every `ingredientData` change.
  // `performSave` (via `ingredientOnSuccess`) reassigns `ingredientData` to
  // the raw PUT-response shape (`{ ingredient_name, ingredient_data }`) after
  // a successful save, which has no top-level `supplierIngredientDetails` —
  // without this guard, that reassignment re-ran this effect with
  // `getSupplierIngredientDetails` undefined, which made `saveValues` fall
  // back `supplier_data.dataType` to `IngredientDataType.USER` and flip
  // `viewEnvironmentalData` to false via `shouldEnableEnvironmentalData`.
  // That unmounted `LcaDataSection` (nulling `lcaRef.current`) while the
  // in-flight `PUT /supplier/:id` was still resolving, so `updateLcaData`
  // (useSupplierSave.ts) silently skipped `POST /bill-of-material-summary`
  // once its `onSuccess` fired — the "Impact results" values were entered
  // and computed correctly but the ref was already gone by save time.
  const hydratedRowRef = useRef<unknown>(undefined);
  useEffect(() => {
    if (!ingredientData || hydratedRowRef.current === ingredientIdToUpdate) return;
    hydratedRowRef.current = ingredientIdToUpdate;

    resetFields();
    setValue("supplier_name", (ingredientData.supplier_name as string) || "");
    saveValues(
      ingredientData,
      ingredientData.supplierIngredientDetails as Record<string, unknown> | undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredientData, ingredientIdToUpdate]);

  useLayoutEffect(
    () => setValue("ingredient_type", lockedIngredientType),
    [setValue, lockedIngredientType]
  );
  useLayoutEffect(() => setValue("unit_type", defaultType), [setValue, defaultType]);
  useLayoutEffect(() => setValue("country", String(country)), [setValue, country]);
  useLayoutEffect(() => setValue("produce_type", produceType), [setValue, produceType]);
  useLayoutEffect(() => setValue("farming_type", farmingType), [setValue, farmingType]);
  useLayoutEffect(() => setValue("field_type", fieldType), [setValue, fieldType]);
  useLayoutEffect(
    () => setValue("specify_ingredient", specifyIngredientValue),
    [setValue, specifyIngredientValue]
  );
  useLayoutEffect(() => setValue("data_type", lockedDataType), [setValue, lockedDataType]);

  const [formSyncKey, setFormSyncKey] = useState(0);
  useLayoutEffect(() => {
    setFormSyncKey(key => key + 1);
  }, [
    lockedIngredientType,
    defaultType,
    country,
    produceType,
    farmingType,
    fieldType,
    specifyIngredientValue,
    lockedDataType
  ]);

  useEffect(() => {
    if (ingredientData?.supplierIngredientDetails) {
      const details = ingredientData.supplierIngredientDetails as Record<string, unknown>;
      if (details?.data_type) setViewEnvironmentalData(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ingredientData]);

  const closeModal = useCallback(() => {
    refetchIngredients();
    setVisibleModal(false);
  }, [refetchIngredients, setVisibleModal]);

  const performSave = useCallback(async () => {
    const documents = lcaRef.current?.getUploadedDocuments?.() || [];
    await new Promise<void>((resolve, reject) => {
      handleSubmit((data: Record<string, unknown>) => {
        if (documents.length > 0) data.supportingDocuments = documents;
        saveData(data, () => resolve(), err => reject(err));
      })();
    });
  }, [handleSubmit, saveData]);

  const isIngredientMappingMode = lockedDataType === IngredientDataType.USER;

  const isGenericCalculationReady = IngredientRegisterButtonState.isGenericCalculationReady(
    lockedDataType,
    viewEnvironmentalData,
    validateData
  );

  const onSave = useCallback(async () => {
    try {
      await performSave();
      if (isIngredientMappingMode) {
        closeModal();
      } else if (isGenericCalculationReady) {
        setCalculateModalOpen(true);
      } else {
        closeModal();
      }
    } catch {
      errorView("An error occurred while saving the ingredient data. Please try again.");
    }
  }, [performSave, isIngredientMappingMode, isGenericCalculationReady, closeModal]);

  const onDataTypeChange = useCallback(
    (type: string) => {
      if (type === IngredientDataType.SUPPLIER && !supplierAsUser) {
        errorView("Supplier is not an user in the system");
        return;
      }
      isDataTypeUserEditRef.current = true;
      setDataType(type);
    },
    [supplierAsUser, setDataType]
  );

  const onIngredientTypeChange = useCallback(
    (type: string) => {
      isIngredientTypeUserEditRef.current = true;
      setIngredientType(type);
    },
    [setIngredientType]
  );

  const { mutate: putIngredientDataToQueue, isLoading: isLoadingPutDataToQueue } =
    usePutIngredientDataToQueue();

  const onApproveCalculate = useCallback(() => {
    putIngredientDataToQueue(
      { ingredientId: ingredientId as string, supplierId: supplierId as string },
      {
        onSuccess: () => {
          setCalculateModalOpen(false);
          refetchIngredients();
          setVisibleModal(false);
        },
        onError: () => setCalculateModalOpen(false)
      }
    );
  }, [putIngredientDataToQueue, ingredientId, supplierId, refetchIngredients, setVisibleModal]);

  const onNavigateIngredientMapping = useCallback(() => {
    navigation(
      `/mapping/${MappingSourceType.ingredient}?sourceId=${ingredientId}&ownerId=${supplierId}&ingredientName=${ingredientData?.ingredient_name}`
    );
  }, [navigation, ingredientId, supplierId, ingredientData]);

  return {
    // Ingredient (Card 1) — no search in edit mode; kept as inert
    // placeholders purely so this satisfies IngredientRegisterViewModel.
    searchControl,
    searchErrors,
    ingredientData: {
      ingredient_name: String(ingredientData?.ingredient_name || ""),
      ingredient_data: ingredientData || {}
    },
    ingredientOptions: [],
    isCreatingNewIngredient: false,
    newIngredientName: "",
    newIngredientError: undefined,
    isCreatingIngredient: false,
    imagePath,
    onSelectIngredient: () => {},
    onNewIngredientNameChange: () => {},
    onIngredientNameChange,
    onStartCreateIngredient: () => {},
    onCancelCreateIngredient: () => {},
    onCreateIngredient: () => {},
    onImageUpload,
    onImageDelete,

    // Supplier search (Card 1) — also not applicable in edit mode.
    supplierData: supplierData as Record<string, unknown>,
    supplierOptions: [],
    enableSupplierSearch: false,
    onSelectSupplier: () => {},

    // Main form (Card 2 + produceCultivar in Card 3)
    register,
    control,
    errors,
    formSyncKey,
    showUnitDetails: !isEmpty(supplierData),
    defaultType,
    onDefaultTypeChange: setDefaultType,
    unitTypeOptions: IngredientRegisterOptions.unitTypes(),

    // Environmental data (Card 3)
    isEnableEnvironmentData,
    viewEnvironmentalData,
    onToggleEnvironmentalData: setViewEnvironmentalData,
    ingredientType: lockedIngredientType,
    onIngredientTypeChange,
    ingredientTypeOptions: IngredientRegisterOptions.ingredientTypes(),
    effectiveDataType: lockedDataType,
    onDataTypeChange,
    dataTypeOptions: IngredientRegisterOptions.dataTypes(supplierData?.supplier_type, lockedDataType),
    showRawFields: packageFlags.produceIngredient,
    showProcessedFields: packageFlags.processIngredient,
    produceType,
    onProduceTypeChange: setProduceType,
    produceTypeOptions: IngredientRegisterOptions.produceTypes(
      isProduceTypesSuccess
        ? (produceTypesData as unknown as Array<{ id: number; produce_name: string }>)
        : undefined
    ),
    isProduceTypesLoading,
    farmingType,
    onFarmingTypeChange: setFarmingType,
    farmingTypeOptions: IngredientRegisterOptions.farmingTypes(),
    fieldType,
    onFieldTypeChange: setFieldType,
    fieldTypeOptions: IngredientRegisterOptions.fieldTypes(),
    specifyIngredientValue,
    onSpecifyIngredientChange: setSpecifyIngredientValue,
    specifyIngredientOptions: IngredientRegisterOptions.specifyIngredients(specifyIngredientData),
    country,
    onCountryChange: setCountry,
    countryOptions: IngredientRegisterOptions.countries(
      isCountriesDataSuccess
        ? (countriesData as unknown as Array<{ id: number; country: string }>)
        : undefined
    ),
    isCountriesDataLoading,

    // New-supplier sub-flow — unreachable in edit mode (Supplier Name shows
    // read-only, no search to trigger "+ New" from), wired for completeness.
    newSupplier,
    registerNewSupplier,
    controlNewSupplier,
    errorsNewSupplier,
    handleSubmitNewSupplier,
    supplierCountry,
    onSupplierCountryChange: setSupplierCountry,
    isSupplierAsUser,
    onIsSupplierAsUserChange: setIsSupplierAsUser,
    onSubmitNewSupplier: handleSupplierSave,
    onCancelNewSupplier: closeSupplierData,

    // Never blocks in edit mode — see the file doc comment.
    isLoadingSupplierData: !isSalesUnitDataSuccess && isSupplierExist,

    // Legacy data sections (Packaging / Transportation / LCA)
    ingredientId,
    supplierId,
    enablePackageData: IngredientRegisterButtonState.enablePackageData(
      true,
      packageFlags,
      viewEnvironmentalData
    ),
    enableTransportData: IngredientRegisterButtonState.enableTransportData(
      true,
      packageFlags,
      viewEnvironmentalData
    ),
    enableLCAData: IngredientRegisterButtonState.enableLCAData(packageFlags, viewEnvironmentalData),
    ingredientPackageData,
    refetchPackagingData,
    lcaRef,

    // Footer / save
    hideIngredientView,
    isIngredientMappingMode,
    enableIngredientMapping: IngredientRegisterButtonState.enableIngredientMapping(
      lockedDataType,
      ingredientId,
      supplierId,
      viewEnvironmentalData
    ),
    enableIngredientButton: IngredientRegisterButtonState.enableIngredientButton(
      supplierData?.supplier_data?.dataType
    ),
    disableCalculateButton: IngredientRegisterButtonState.disableCalculateButton(
      isSupplierFormOpen,
      supplierValidation,
      currentSupplierId
    ),
    genericButtonTitle: IngredientRegisterButtonState.genericButtonTitle(isGenericCalculationReady),
    onSave,
    onNavigateIngredientMapping,
    onCancel: closeModal,

    // Calculate confirmation
    calculateModalOpen,
    onCloseCalculateModal: () => setCalculateModalOpen(false),
    onApproveCalculate,
    isLoadingPutDataToQueue
  };
};
